import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import './ExecutionLog.css';

const NODE_TITLE_RU = {
  light_router: 'Лёгкий роутер',
  orchestrator: 'Оркестратор',
  calendar_agent: 'Календарь',
  financial_agent: 'Финансы',
  web_search_agent: 'Веб-поиск',
  memory_agent: 'Память',
  memory_lookup: 'Память (cold start)',
  llm: 'Модель',
};

function SectionToggle({ title, open, onToggle }) {
  return (
    <button type="button" className="ExecutionLog-sectionToggle" onClick={onToggle} aria-expanded={open}>
      <span className="ExecutionLog-llmToggleIcon">{open ? '▼' : '▶'}</span>
      <span>{title}</span>
    </button>
  );
}

function roleToRowClass(role) {
  if (role === 'human') return 'ExecutionLog-chatRow--user';
  return 'ExecutionLog-chatRow--ai';
}

function roleToBubbleClass(role) {
  if (role === 'human') return 'ExecutionLog-bubble--user';
  if (role === 'ai') return 'ExecutionLog-bubble--ai';
  return 'ExecutionLog-bubble--other';
}

function ChatTimeline({ messages }) {
  const src = Array.isArray(messages) ? messages : [];
  const items = src;
  if (items.length === 0) return <div className="ExecutionLog-emptyBlock">Нет сообщений.</div>;
  return (
    <div className="ExecutionLog-chatPreview">
      {items.map((m, i) => (
        <div key={`${m.role || 'message'}-${i}`} className={`ExecutionLog-chatRow ${roleToRowClass(m.role)}`}>
          <div className={`ExecutionLog-bubble ${roleToBubbleClass(m.role)}`}>{m.text || '—'}</div>
        </div>
      ))}
    </div>
  );
}

function LlmBlock({ block, index }) {
  const [open, setOpen] = useState(false);
  const [openSystem, setOpenSystem] = useState(false);
  const [openUser, setOpenUser] = useState(false);
  const raw = block.node || '';
  const title = NODE_TITLE_RU[raw] || raw || `Шаг ${index + 1}`;
  const timeline = Array.isArray(block.prompt_messages) ? block.prompt_messages : [];
  return (
    <div className={`ExecutionLog-llmBlock ${open ? 'ExecutionLog-llmBlock--open' : 'ExecutionLog-llmBlock--collapsed'}`}>
      <button
        type="button"
        className="ExecutionLog-llmNodeTitle ExecutionLog-llmNodeTitle--toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ExecutionLog-llmToggleIcon">{open ? '▼' : '▶'}</span>
        <span className="ExecutionLog-llmNodeTitleText">{title}</span>
      </button>
      {open && (
        <>
          <div className="ExecutionLog-sectionsWrap">
            <div className="ExecutionLog-box ExecutionLog-box--system">
              <SectionToggle
                title="Системный промпт"
                open={openSystem}
                onToggle={() => setOpenSystem((v) => !v)}
              />
              {openSystem && (
                <pre className="ExecutionLog-boxBody ExecutionLog-boxBody--expanded">{block.system || '—'}</pre>
              )}
            </div>

            <div className="ExecutionLog-box ExecutionLog-box--human">
              <SectionToggle
                title="Пользователь / контекст"
                open={openUser}
                onToggle={() => setOpenUser((v) => !v)}
              />
              {!openUser && (
                <ChatTimeline messages={timeline} />
              )}
              {openUser && (
                <ChatTimeline messages={timeline} />
              )}
            </div>
          </div>
          <div className="ExecutionLog-box ExecutionLog-box--ai">
            <div className="ExecutionLog-boxLabel">Ответ модели</div>
            <pre className="ExecutionLog-boxBody ExecutionLog-boxBody--expanded">{block.response || '—'}</pre>
          </div>
        </>
      )}
    </div>
  );
}

function RunCard({ run, runKey }) {
  const [open, setOpen] = useState(false);
  const t = run.created_at
    ? new Date(run.created_at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' })
    : '—';
  const blocks = run.blocks || [];
  const kindLabel = run.agent_kind === 'memory' ? 'Память' : 'Главный граф';
  const phaseLabel = run.phase === 'primary' ? 'основной' : run.phase;

  return (
    <article className="ExecutionLog-runCard">
      <button
        type="button"
        className="ExecutionLog-runCardHeader ExecutionLog-runCardHeader--toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ExecutionLog-llmToggleIcon">{open ? '▼' : '▶'}</span>
        <span className="ExecutionLog-runCardTime">{t}</span>
        <span className="ExecutionLog-runCardBadge ExecutionLog-runCardBadge--kind">{kindLabel}</span>
        <span className="ExecutionLog-runCardBadge ExecutionLog-runCardBadge--phase">{phaseLabel}</span>
        <span className="ExecutionLog-runCardMeta">
          {blocks.length} {blocks.length === 1 ? 'вызов LLM' : 'вызовов LLM'}
        </span>
      </button>
      {open && (
        <>
          {run.error && <div className="ExecutionLog-runError">Ошибка потока: {run.error}</div>}
          <div className="ExecutionLog-runCardBody">
            {blocks.length === 0 && <div className="ExecutionLog-emptyBlock">Нет записей LLM для этого запуска.</div>}
            {blocks.map((b, i) => (
              <LlmBlock key={`${runKey}-${i}`} block={b} index={i} />
            ))}
          </div>
        </>
      )}
    </article>
  );
}

export default function ExecutionLog() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterThreadId, setFilterThreadId] = useState('');
  const [filterRunId, setFilterRunId] = useState('');
  const [limit, setLimit] = useState(200);
  const initialFetchDone = useRef(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterThreadId.trim()) params.set('thread_id', filterThreadId.trim());
      if (filterRunId.trim()) params.set('run_id', filterRunId.trim());
      params.set('limit', String(limit));
      const res = await apiFetch(`/api/agent-calendar/execution-runs/?${params}`);
      if (!res.ok) throw new Error('Ошибка загрузки');
      const data = await res.json();
      setRuns(data.results || []);
    } catch (e) {
      setError(e.message || 'Ошибка');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [filterThreadId, filterRunId, limit]);

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    fetchRuns();
  }, [fetchRuns]);

  return (
    <div className="ExecutionLog">
      <div className="ExecutionLog-toolbar">
        <label>
          <span>Фильтр чата (thread_id)</span>
          <input
            type="text"
            value={filterThreadId}
            onChange={(e) => setFilterThreadId(e.target.value)}
            placeholder="например tg_12345"
            className="ExecutionLog-input"
          />
        </label>
        <label>
          <span>Фильтр run_id</span>
          <input
            type="text"
            value={filterRunId}
            onChange={(e) => setFilterRunId(e.target.value)}
            placeholder="опционально"
            className="ExecutionLog-input"
          />
        </label>
        <label>
          <span>Лимит</span>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="ExecutionLog-select">
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
        </label>
        <button type="button" onClick={() => fetchRuns()} className="ExecutionLog-btn">
          Обновить
        </button>
      </div>

      {error && <div className="ExecutionLog-error">{error}</div>}
      {loading && <div className="ExecutionLog-loading">Загрузка…</div>}

      {!loading && !error && (
        <div className="ExecutionLog-runList">
          {runs.length === 0 && (
            <div className="ExecutionLog-empty">Нет записей. Отправьте сообщение боту или в веб-чат.</div>
          )}
          {runs.map((run, idx) => (
            <RunCard key={`${run.created_at}-${run.agent_kind}-${run.phase}-${idx}`} run={run} runKey={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
